import { axiosInstance } from "api/axios";
import { IFinancialOperation } from "dto/Banking/IBanking";
import { LinkedInvoice } from "./ContractorDetailPanel";

// ── Typy ─────────────────────────────────────────────────────────────────────
// ContractorRow jest zdefiniowane tutaj (nie w index.tsx), żeby uniknąć
// kołowego importu między index.tsx a tym modułem utility.

export interface ContractorRow {
  id: string;
  name: string;
  nip: string;
  ibans: string[];
  docCount: number;
  sources: string[];
  invoices: LinkedInvoice[];
  bankOps: IFinancialOperation[];
  acctCode?: string;
  proposals?: import("./nipProposals").ProposalEntry[];
  proposalLoading?: boolean;
}

// ── Pobieranie wszystkich stron KSeF ─────────────────────────────────────────

export async function fetchAllKSeFPages(list: 0 | 1): Promise<unknown[]> {
  const result: unknown[] = [];
  let page = 0;
  const LIMIT = 100;
  while (page < LIMIT) {
    const res = await axiosInstance.get(
      `/ksef/invoices?List=${list}&Page=${page}&SortProp=Date&SortOrder=Descending`,
    );
    const items: unknown[] = res.data?.Invoices ?? [];
    result.push(...items);
    if (!res.data?.HasMore) break;
    page++;
  }
  return result;
}

// ── Mapa kont FK z planu kont / bilansu otwarcia ─────────────────────────────

const CONTRACTOR_ACCT_RE = /^2[01]1-\d+/;

export function loadFkAccountLookup(): Map<string, string> {
  const codeToName = new Map<string, string>();

  try {
    const raw = localStorage.getItem("zois_contractors_rows");
    if (raw) {
      const { entries } = JSON.parse(raw) as { entries: { code: string; name: string }[] };
      entries.forEach((e) => {
        if (e.code && e.name && CONTRACTOR_ACCT_RE.test(e.code.toLowerCase()))
          codeToName.set(e.code.toLowerCase(), e.name);
      });
    }
  } catch { /* ignore */ }

  if (codeToName.size === 0) {
    try {
      const raw = localStorage.getItem("zois_opening_balance_v1");
      if (raw) {
        const { entries } = JSON.parse(raw) as { entries: { code: string; name: string }[] };
        entries
          .filter((e) => CONTRACTOR_ACCT_RE.test(e.code.toLowerCase()))
          .forEach((e) => { if (e.name) codeToName.set(e.code.toLowerCase(), e.name); });
      }
    } catch { /* ignore */ }
  }

  const result = new Map<string, string>();
  for (const [code, name] of codeToName) {
    result.set(name.toLowerCase().trim(), code);
  }
  return result;
}

export function lookupFkAcct(name: string, lookup: Map<string, string>): string | undefined {
  if (!name || !lookup.size) return undefined;
  const key = name.toLowerCase().trim();
  if (lookup.has(key)) return lookup.get(key);
  for (const [k, v] of lookup) {
    if (key.length >= 4 && k.length >= 4 && (key.includes(k) || k.includes(key))) return v;
  }
  return undefined;
}

// ── Łączenie transakcji z wierszami kartoteki DB ──────────────────────────────
// Używane przez ContractorsCardRegister. Startuje od gotowych wierszy DB (nie
// tworzy nowych wpisów dla nieznanych kontrahentów — niezmatchowane transakcje
// są ignorowane). Dopasowanie: NIP → IBAN → nazwa kontrahenta.

const normNip  = (s: string) => (s ?? "").replace(/\D/g, "");
const normIban = (s: string) => (s ?? "").replace(/\s/g, "").toUpperCase();

export function linkTransactionsToRows(
  dbRows: ContractorRow[],
  purchaseInvoices: unknown[],
  salesInvoices: unknown[],
  registryInvoices: unknown[],
  bankOps: IFinancialOperation[],
): ContractorRow[] {
  // Klonujemy wiersze — zerujemy dane transakcyjne (źródła Kartoteka/Migracja zostają)
  const rows: ContractorRow[] = dbRows.map(r => ({
    ...r,
    docCount: 0,
    sources: r.sources.filter(s => s === "Kartoteka" || s === "Migracja"),
    invoices: [],
    bankOps: [],
  }));

  // Indeksy do wyszukiwania (budowane raz dla całej listy)
  const byNip  = new Map<string, ContractorRow>();
  const byIban = new Map<string, ContractorRow>();
  const byName = new Map<string, ContractorRow>();

  for (const row of rows) {
    const nip = normNip(row.nip);
    if (nip.length >= 10) byNip.set(nip.slice(0, 10), row);
    for (const ib of row.ibans) byIban.set(normIban(ib), row);
    const nameKey = row.name.toLowerCase().trim();
    if (nameKey) byName.set(nameKey, row);
  }

  const find = (nip?: string, ibanRaw?: string): ContractorRow | null => {
    if (nip) {
      const n = normNip(nip);
      if (n.length === 10 && byNip.has(n)) return byNip.get(n)!;
    }
    if (ibanRaw) {
      const ib = normIban(ibanRaw);
      if (ib && byIban.has(ib)) return byIban.get(ib)!;
    }
    return null;
  };

  const addSrc = (row: ContractorRow, source: string) => {
    if (!row.sources.includes(source)) row.sources.push(source);
  };

  const pushInv = (row: ContractorRow, inv: LinkedInvoice) => {
    row.docCount++;
    row.invoices.push(inv);
  };

  // ── Faktury zakupu KSeF (sprzedawca = kontrahent) ──────────────────────────
  for (const inv of purchaseInvoices as Record<string, unknown>[]) {
    const seller = inv.Seller as Record<string, string> | undefined;
    const row = find(seller?.Nip, String(inv.BankAccount ?? ""));
    if (!row) continue;
    addSrc(row, "Zakup");
    pushInv(row, {
      id: String(inv.Id ?? ""),
      number: String(inv.InvoiceNumber ?? inv.KsefNumber ?? inv.Id ?? ""),
      date: String(inv.IssueDate ?? ""),
      grossAmount: Number(inv.GrossAmount ?? inv.Amount ?? 0),
      currency: String(inv.Currency ?? "PLN"),
      type: "Zakup",
      ksefNumber: inv.KsefNumber ? String(inv.KsefNumber) : undefined,
    });
  }

  // ── Faktury sprzedaży KSeF (nabywca = kontrahent) ──────────────────────────
  for (const inv of salesInvoices as Record<string, unknown>[]) {
    const buyer = inv.Buyer as Record<string, unknown> | undefined;
    const buyerNip = (buyer?.Identifier as Record<string, string> | undefined)?.Value ?? "";
    const row = find(buyerNip, String(inv.BankAccount ?? ""));
    if (!row) continue;
    addSrc(row, "Sprzedaż");
    pushInv(row, {
      id: String(inv.Id ?? ""),
      number: String(inv.InvoiceNumber ?? inv.KsefNumber ?? inv.Id ?? ""),
      date: String(inv.IssueDate ?? ""),
      grossAmount: Number(inv.GrossAmount ?? inv.Amount ?? 0),
      currency: String(inv.Currency ?? "PLN"),
      type: "Sprzedaż",
      ksefNumber: inv.KsefNumber ? String(inv.KsefNumber) : undefined,
    });
  }

  // ── Faktury z rejestru ──────────────────────────────────────────────────────
  for (const inv of registryInvoices as Record<string, unknown>[]) {
    const row = find(String(inv.IssuerNIP ?? ""), String(inv.AccountNumber ?? ""));
    if (!row) continue;
    addSrc(row, "Rejestr");
    pushInv(row, {
      id: String(inv.Id ?? ""),
      number: String(inv.InvoiceNumber ?? inv.Id ?? ""),
      date: String(inv.IssueDate ?? ""),
      grossAmount: Number(inv.GrossAmount ?? inv.NetAmount ?? inv.Amount ?? 0),
      currency: String(inv.Currency ?? "PLN"),
      type: "Rejestr",
      ksefNumber: inv.KsefNumber ? String(inv.KsefNumber) : undefined,
      fileUri: inv.FileUri ? String(inv.FileUri) : undefined,
      encryptionKeyId: inv.EncryptionKeyId ? String(inv.EncryptionKeyId) : undefined,
      fileName: inv.FileName ? String(inv.FileName) : undefined,
      objectKeys: (inv.ObjectKeys as import("dto/IKey").IKey[] | undefined) ?? undefined,
    });
  }

  // ── Operacje bankowe — dopasowanie po IBAN, fallback po nazwie ──────────────
  for (const op of bankOps) {
    const source = op.Direction === "Credit" ? "Wpływ" : "Wypływ";
    const ib   = normIban(op.CounterpartyIban ?? "");
    const name = (op.CounterpartyName ?? "").trim().toLowerCase();

    const matched = (ib && byIban.get(ib)) ?? (name && byName.get(name)) ?? null;
    if (!matched) continue;

    addSrc(matched, source);
    matched.docCount++;
    matched.bankOps.push(op);
  }

  return rows;
}

// ── Obliczanie obrotów Wn/Ma/saldo z powiązanych transakcji ──────────────────

export function computeContractorAmounts(row: ContractorRow): {
  kwotaWn: number;
  kwotaMa: number;
  saldo: number;
} {
  let kwotaMa = 0;
  let kwotaWn = 0;

  for (const inv of row.invoices) {
    const amt = inv.grossAmount ?? 0;
    if (inv.type === "Zakup" || inv.type === "Rejestr") {
      kwotaMa += amt; // zobowiązanie wobec dostawcy → Ma (konto 211)
    } else {
      kwotaWn += amt; // należność od odbiorcy → Wn (konto 201)
    }
  }

  for (const op of row.bankOps) {
    const amt = op.Amount ?? 0;
    if (op.Direction === "Debit") {
      kwotaWn += amt; // wypływ = spłata zobowiązania → Wn (211)
    } else {
      kwotaMa += amt; // wpływ = zapłata od klienta → Ma (201)
    }
  }

  return { kwotaWn, kwotaMa, saldo: kwotaWn - kwotaMa };
}
